/* LUZ NOVA - collection page filters (Gender / Category / Style / Size / Brand / Price).
   Moved out of assets/global.js on 4 Oct 2026 and extended:
     - size checkboxes send the real variant values ("US W 8" / "US M 8")
     - women's / men's / clothing size groups follow the page's gender+category
     - brand pages keep their brand when hopping to Women / Men
     - mobile drawer: live "Show N products" button, count badge, scroll lock
   Loaded by sections/main-collection.liquid (deferred, so it runs after
   global.js and plp-ajax.js). Adding a Style option still needs three edits:
   the smart collection, an <li> in the section, and an entry in STYLE_INFO below. */
(function () {
  "use strict";

  /* ====================================================================
     CUSTOM COLLECTION FILTERS — restores checkbox/price state from the
     URL on load (since we build our own filter list instead of reading
     collection.filters.active_values), and renders removable "active
     filter" pills. The actual filtering is still done server-side by
     Shopify via the native filter.* URL params — this just drives the UI.
     ==================================================================== */
  (function () {
    /* The section renders the form as #FacetFormPending so that the older
       copy of this logic still sitting in assets/global.js (now dead code -
       its IIFE exits early when it cannot find #FacetForm) never binds to it.
       Claim the real id here so assets/plp-ajax.js, which looks the form up
       by id at submit time, keeps working. */
    const form = document.getElementById("FacetFormPending") || document.getElementById("FacetForm");
    if (!form) return;
    form.id = "FacetForm";

    /* Auto-submit: any non-navigating control (size, brand, price, stock)
       re-filters in place. Gender/Category/Style checkboxes stop propagation
       and navigate to a different collection instead. */
    form.addEventListener("change", () => {
      if (window.LuzAjaxFacets) { window.LuzAjaxFacets.submit(); } else { form.submit(); }
    });

    const minInput = form.querySelector("[data-price-min]");
    const maxInput = form.querySelector("[data-price-max]");

    /* Re-syncs checkbox/price-input state from the current URL. Runs once on
       initial load (see the bottom of this IIFE) and is re-run by
       assets/plp-ajax.js after every AJAX filter/sort/page/back-button change.
       assets/plp-ajax.js deliberately never replaces this form itself (the
       shopper's own in-progress toggles stay exactly as they left them) — the
       one case that needs catching up is browser back/forward, where the URL
       moves without anyone touching a checkbox. Unlike a one-shot init this
       always assigns both true and false so repeat calls stay correct. */
    function restoreFromUrl() {
      const params = new URLSearchParams(window.location.search);
      form.querySelectorAll('input[type="checkbox"]:not(.gender-checkbox):not(.category-checkbox):not(.style-checkbox)').forEach((cb) => {
        cb.checked = params.getAll(cb.name).includes(cb.value);
      });
      if (minInput) minInput.value = params.get(minInput.name) || "";
      if (maxInput) maxInput.value = params.get(maxInput.name) || "";
      form.querySelectorAll("details").forEach((d) => {
        const hasChecked = d.querySelector('input[type="checkbox"]:checked');
        const hasPrice = d.contains(minInput) && (minInput?.value || maxInput?.value);
        d.open = d.open || !!(hasChecked || hasPrice);
      });
    }

    form.querySelectorAll("[data-price-preset]").forEach((btn) => {
      btn.addEventListener("click", () => {
        if (minInput) minInput.value = btn.dataset.min || "";
        if (maxInput) maxInput.value = btn.dataset.max || "";
        if (window.LuzAjaxFacets) { window.LuzAjaxFacets.submit(); } else { form.submit(); }
      });
    });

    /* Gender + Category: both navigate between the store's existing
       collections (men, women, footwear, clothing, accessories, and
       combo collections like men-footwear) instead of injecting
       filter.p.product_type — that filter silently does nothing until
       Search & Discovery's "Product type" filter is enabled in Admin
       (Settings → Apps → Search & Discovery → Filters → Edit filters).
       Linking to the real collections works today with no admin step,
       and preserves every other active filter param across the jump.
       HANDLE_INFO maps every collection this feature can land on back
       to its (gender, category) state so checkboxes restore correctly
       and switching one dimension recombines with whichever value is
       already active in the other. */
    const HANDLE_INFO = {
      all: { gender: "", category: "" },
      men: { gender: "men", category: "" },
      women: { gender: "women", category: "" },
      footwear: { gender: "", category: "footwear" },
      clothing: { gender: "", category: "clothing" },
      accessories: { gender: "", category: "accessories" },
      "men-footwear": { gender: "men", category: "footwear" },
      "men-clothing": { gender: "men", category: "clothing" },
      "men-accessories": { gender: "men", category: "accessories" },
      "women-footwear": { gender: "women", category: "footwear" },
      "women-clothing": { gender: "women", category: "clothing" },
      "women-accessories": { gender: "women", category: "accessories" },
    };

    /* ---------- STYLE (sub-category) ----------
       Gender + Category alone can't answer "show me leggings". Our catalog
       has no sub-category data to filter on: product_type is only the six
       coarse values (Mens/Womens x Footwear/Clothing/Accessories), and the
       product tags are supplier-feed noise (sku:, Scraped_, gemini_...).
       The only reliable signal for "this is a legging" is the product title.

       Shopify's native filters cannot match on title, so instead each style
       is a smart collection with a TITLE CONTAINS rule (created 2026-08-10).
       Selecting a style navigates to that collection and carries every other
       active filter param along, exactly like Gender and Category do — so
       Brand, Price, Size and Availability keep working on top of it, and each
       style gets its own crawlable URL (/collections/womens-leggings).

       `key` is what makes a style survive a gender switch: toggling Men on
       womens-hoodies lands on mens-hoodies rather than dumping the shopper
       back into the whole Men collection. */
    const STYLE_INFO = {
      "womens-leggings":    { gender: "women", category: "clothing",    key: "leggings" },
      "womens-sports-bras": { gender: "women", category: "clothing",    key: "sports-bras" },
      "womens-crop-tops":   { gender: "women", category: "clothing",    key: "crop-tops" },
      "womens-t-shirts":    { gender: "women", category: "clothing",    key: "t-shirts" },
      "womens-hoodies":     { gender: "women", category: "clothing",    key: "hoodies" },
      "womens-sweatshirts": { gender: "women", category: "clothing",    key: "sweatshirts" },
      "womens-jackets":     { gender: "women", category: "clothing",    key: "jackets" },
      "womens-shorts":      { gender: "women", category: "clothing",    key: "shorts" },
      "womens-pants":       { gender: "women", category: "clothing",    key: "pants" },
      "womens-skirts":      { gender: "women", category: "clothing",    key: "skirts" },
      "mens-t-shirts":      { gender: "men",   category: "clothing",    key: "t-shirts" },
      "mens-polos":         { gender: "men",   category: "clothing",    key: "polos" },
      "mens-hoodies":       { gender: "men",   category: "clothing",    key: "hoodies" },
      "mens-sweatshirts":   { gender: "men",   category: "clothing",    key: "sweatshirts" },
      "mens-jackets":       { gender: "men",   category: "clothing",    key: "jackets" },
      "mens-shorts":        { gender: "men",   category: "clothing",    key: "shorts" },
      "mens-pants":         { gender: "men",   category: "clothing",    key: "pants" },
      "mens-track-pants":   { gender: "men",   category: "clothing",    key: "track-pants" },
      "mens-joggers":       { gender: "men",   category: "clothing",    key: "joggers" },
      "socks":              { gender: "",      category: "accessories", key: "socks" },
      "caps-hats":          { gender: "",      category: "accessories", key: "caps-hats" },
      "beanies":            { gender: "",      category: "accessories", key: "beanies" },
      "backpacks":          { gender: "",      category: "accessories", key: "backpacks" },
      "bags":               { gender: "",      category: "accessories", key: "bags" },
      "slides-sandals":     { gender: "",      category: "footwear",    key: "slides" },
      "for-runners":        { gender: "",      category: "footwear",    key: "running" },
      "basketball-sneakers":{ gender: "",      category: "footwear",    key: "basketball" },
    };

    /* Style collections are also valid landing pages for Gender/Category, so
       fold them into HANDLE_INFO — otherwise those checkboxes would render
       unchecked on /collections/womens-leggings. */
    Object.keys(STYLE_INFO).forEach((h) => {
      if (!HANDLE_INFO[h]) {
        HANDLE_INFO[h] = { gender: STYLE_INFO[h].gender, category: STYLE_INFO[h].category };
      }
    });

    /* key -> { men: handle, women: handle } for gender swapping. */
    const STYLE_BY_KEY = {};
    Object.keys(STYLE_INFO).forEach((h) => {
      const s = STYLE_INFO[h];
      STYLE_BY_KEY[s.key] = STYLE_BY_KEY[s.key] || {};
      STYLE_BY_KEY[s.key][s.gender || "any"] = h;
    });

    const collectionHandle = form.dataset.collectionHandle || "";
    const current = HANDLE_INFO[collectionHandle] || { gender: "", category: "" };
    const currentStyle = STYLE_INFO[collectionHandle] || null;

    /* Carries the active filter params to the next collection. Two extras:
       1. A brand page (/collections/asics) has no filter.p.vendor param - the
          brand IS the collection - so hopping to /collections/women would
          silently drop ASICS. data-carry-vendor (set in Liquid only when the
          collection has exactly one vendor) turns it into a real brand filter.
       2. Size values are gender-specific ("US W 8" vs "US M 8"). When the
          target gender is known, drop the other gender's sizes so the shopper
          never lands on a page filtered by sizes that cannot match. */
    function withParams(path, gender) {
      const p = new URLSearchParams(window.location.search);
      p.delete("page");
      if (gender === "men" || gender === "women") {
        const drop = gender === "men" ? "US W " : "US M ";
        const keep = p.getAll("filter.v.option.size").filter((v) => v.indexOf(drop) !== 0);
        p.delete("filter.v.option.size");
        keep.forEach((v) => p.append("filter.v.option.size", v));
      }
      const carry = form.dataset.carryVendor;
      if (carry && !HANDLE_INFO[collectionHandle] && !p.has("filter.p.vendor")) {
        p.set("filter.p.vendor", carry);
      }
      const qs = p.toString();
      return path + (qs ? "?" + qs : "");
    }

    function navigateTo(gender, category) {
      const target = gender && category ? gender + "-" + category : (gender || category || "all");
      window.location.href = withParams("/collections/" + target, gender);
    }

    function navigateToStyle(handle) {
      const info = STYLE_INFO[handle];
      window.location.href = withParams("/collections/" + handle, info ? info.gender : "");
    }

    /* Size groups: show women's sizes on Women pages, men's on Men pages, both
       when gender is unknown; hide shoe sizes on clothing/accessories pages and
       clothing sizes on footwear pages. */
    form.querySelectorAll("[data-size-group]").forEach((d) => {
      const g = d.dataset.sizeGroup;
      let hide = false;
      if (g === "women" && current.gender === "men") hide = true;
      if (g === "men" && current.gender === "women") hide = true;
      if ((g === "women" || g === "men") && (current.category === "clothing" || current.category === "accessories")) hide = true;
      if (g === "clothing" && current.category === "footwear") hide = true;
      d.hidden = hide;
    });

    form.querySelectorAll(".gender-checkbox").forEach((cb) => {
      if (cb.dataset.genderHandle === current.gender) cb.checked = true;
      cb.addEventListener("change", (evt) => {
        evt.stopPropagation();
        const nextGender = cb.checked ? cb.dataset.genderHandle : "";
        /* Keep the style if the same style exists for the newly chosen
           gender (womens-hoodies -> mens-hoodies). Genderless styles like
           socks are unaffected by the gender toggle, so leave them alone. */
        if (currentStyle && currentStyle.gender) {
          const sibling = nextGender && STYLE_BY_KEY[currentStyle.key]
            ? STYLE_BY_KEY[currentStyle.key][nextGender]
            : null;
          if (sibling) { navigateToStyle(sibling); return; }
        }
        navigateTo(nextGender, current.category);
      });
    });

    form.querySelectorAll(".category-checkbox").forEach((cb) => {
      if (cb.dataset.categoryHandle === current.category) cb.checked = true;
      cb.addEventListener("change", (evt) => {
        evt.stopPropagation();
        navigateTo(current.gender, cb.checked ? cb.dataset.categoryHandle : "");
      });
    });

    /* Style list: only offer styles that make sense in the current context.
       On /collections/women we hide men's styles; on a footwear collection we
       hide clothing styles. With no context at all (e.g. /collections/all)
       everything shows, grouped by the labels rendered in Liquid. */
    form.querySelectorAll(".style-checkbox").forEach((cb) => {
      const handle = cb.dataset.styleHandle;
      const info = STYLE_INFO[handle];
      const li = cb.closest("li");
      const isCurrent = handle === collectionHandle;

      if (info && li && !isCurrent) {
        const genderClash = current.gender && info.gender && info.gender !== current.gender;
        const catClash = current.category && info.category && info.category !== current.category;
        if (genderClash || catClash) { li.hidden = true; return; }
      }

      if (isCurrent) cb.checked = true;
      cb.addEventListener("change", (evt) => {
        evt.stopPropagation();
        if (cb.checked) { navigateToStyle(handle); return; }
        navigateTo(current.gender, current.category);
      });
    });

    /* If every style in a group got hidden, hide the group heading too. */
    form.querySelectorAll("[data-style-group]").forEach((group) => {
      const anyVisible = Array.prototype.some.call(
        group.querySelectorAll("li"),
        (li) => !li.hidden
      );
      if (!anyVisible) group.hidden = true;
    });

    /* Rebuilds the removable "active filter" pills. Re-queries #ActiveFacets
       fresh on every call rather than capturing it once: that element lives
       inside the #CollectionResults region that assets/plp-ajax.js replaces
       wholesale on every AJAX update, so a captured-once reference would go
       stale (detached from the document) after the first filter change. */
    function refreshActivePills() {
      const activeWrap = document.getElementById("ActiveFacets");
      if (!activeWrap) return;
      const pills = [];

      form.querySelectorAll('input[type="checkbox"]:checked:not(.gender-checkbox):not(.category-checkbox):not(.style-checkbox)').forEach((cb) => {
        const p = new URLSearchParams(window.location.search);
        const remaining = p.getAll(cb.name).filter((v) => v !== cb.value);
        p.delete(cb.name);
        remaining.forEach((v) => p.append(cb.name, v));
        const label = cb.dataset.pill || cb.closest("label")?.textContent.trim() || cb.value;
        const qs = p.toString();
        pills.push({ label, url: window.location.pathname + (qs ? "?" + qs : "") });
      });

      if (currentStyle) {
        const styleLabel =
          form.querySelector('.style-checkbox[data-style-handle="' + collectionHandle + '"]')
            ?.closest("label")?.textContent.trim() || collectionHandle;
        const qs = new URLSearchParams(window.location.search).toString();
        const fallback =
          current.gender && current.category
            ? current.gender + "-" + current.category
            : current.gender || current.category || "all";
        pills.push({ label: styleLabel, url: "/collections/" + fallback + (qs ? "?" + qs : "") });
      }

      if (current.gender) {
        const qs = new URLSearchParams(window.location.search).toString();
        pills.push({
          label: current.gender === "men" ? "Men" : "Women",
          url: "/collections/" + (current.category || "all") + (qs ? "?" + qs : ""),
        });
      }

      if (current.category) {
        const catLabel = current.category.charAt(0).toUpperCase() + current.category.slice(1);
        const qs = new URLSearchParams(window.location.search).toString();
        pills.push({
          label: catLabel,
          url: "/collections/" + (current.gender || "all") + (qs ? "?" + qs : ""),
        });
      }

      if ((minInput && minInput.value) || (maxInput && maxInput.value)) {
        const p = new URLSearchParams(window.location.search);
        if (minInput) p.delete(minInput.name);
        if (maxInput) p.delete(maxInput.name);
        const qs = p.toString();
        pills.push({ label: "Price", url: window.location.pathname + (qs ? "?" + qs : "") });
      }

      const clearPath = (currentStyle || current.gender || current.category) ? "/collections/all" : window.location.pathname;
      document.querySelectorAll("[data-facets-open]").forEach((b) => { b.dataset.count = String(pills.length); });
      document.querySelectorAll("[data-facets-clear]").forEach((a) => { a.hidden = !pills.length; a.href = clearPath; });

      if (pills.length) {
        activeWrap.hidden = false;
        activeWrap.innerHTML =
          pills.map((p) => '<a href="' + p.url + '">' + p.label.replace(/</g, "&lt;") + " ✕</a>").join("") +
          '<a href="' + clearPath + '">Clear all</a>';
      } else {
        activeWrap.hidden = true;
        activeWrap.innerHTML = "";
      }
    }

    restoreFromUrl();
    refreshActivePills();

    /* Mobile drawer: the apply button always shows the live result count
       (assets/plp-ajax.js rewrites #ProductCount after every AJAX swap), the
       page behind does not scroll while the drawer is open, and Esc closes it. */
    (function () {
      const wrap = document.getElementById("FacetsWrap");
      const countEl = document.getElementById("ProductCount");
      const applyBtn = document.querySelector("[data-facets-apply]");
      if (!wrap) return;
      function syncApply() {
        if (!applyBtn || !countEl) return;
        const t = countEl.textContent.trim();
        applyBtn.textContent = t ? "Show " + t : "Show results";
      }
      syncApply();
      if (countEl && window.MutationObserver) {
        new MutationObserver(syncApply).observe(countEl, { childList: true, characterData: true, subtree: true });
      }
      function syncLock() {
        document.documentElement.style.overflow = wrap.classList.contains("is-open") ? "hidden" : "";
      }
      if (window.MutationObserver) {
        new MutationObserver(syncLock).observe(wrap, { attributes: true, attributeFilter: ["class"] });
      }
      document.addEventListener("keydown", (e) => {
        if (e.key === "Escape" && wrap.classList.contains("is-open")) wrap.classList.remove("is-open");
      });
    })();

    /* Exposed for assets/plp-ajax.js: re-sync this form's own state and the
       active-pills row after every AJAX-driven URL change (filter, sort,
       pagination, pill removal, or browser back/forward). */
    window.LuzFacetSync = { restoreFromUrl, refreshActivePills };
  })();
})();